import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api/api'

const initialState = {
    totalSale: 0,
    totalOrder: 0,
    totalProduct: 0,
    totalPendingOrder: 0,
    totalPaidOrders: 0,
    totalUnpaidOrders: 0,
    totalRefundedOrders: 0,
    totalPendingPaymentOrders: 0,
    totalFailedPaymentOrders: 0,
    totalDeliveredOrders: 0,
    totalCancelledOrders: 0,
    totalProcessingOrders: 0,
    totalNoShippedOrders: 0,
    totalSeller: 0,
    recentOrders: [],

    chartData: {
        orders: Array(12).fill(0),
        revenue: Array(12).fill(0),
        paidRevenue: Array(12).fill(0),
        paidOrders: Array(12).fill(0),
        unpaidOrders: Array(12).fill(0),
        refundedOrders: Array(12).fill(0),
        pendingPaymentOrders: Array(12).fill(0),
        failedPaymentOrders: Array(12).fill(0),
        deliveredOrders: Array(12).fill(0),
        processingOrders: Array(12).fill(0),
        cancelledOrders: Array(12).fill(0),
        pendingOrders: Array(12).fill(0),
        noShippedOrders: Array(12).fill(0),
        sellers: Array(12).fill(0)
    },
    monthlyStats: {
        orderPercentage: 0,
        revenuePercentage: 0,
        productPercentage: 0
    },
    deliveryBreakdown: {
        pending: 0,
        placed: 0,
        processing: 0,
        warehouse: 0,
        ready_to_pickup: 0,
        ready_to_ship: 0,
        shipped: 0,
        no_shipped: 0,
        delivered: 0,
        cancelled: 0
    }
}

export const get_seller_dashboard_index_data = createAsyncThunk(
    'dashboard/get_seller_dashboard_index_data',
    async (filters = {}, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams()
            if (filters.year) params.append('year', filters.year)
            if (filters.month) params.append('month', filters.month)
            
            const url = `/seller/get-dashboard-index-data${params.toString() ? '?' + params.toString() : ''}`
            const { data } = await api.get(url, { withCredentials: true })
            return data
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const get_admin_dashboard_index_data = createAsyncThunk(
    'dashboard/get_admin_dashboard_index_data',
    async (filters = {}, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams()
            if (filters.year) params.append('year', filters.year)
            if (filters.month) params.append('month', filters.month)
            if (filters.sellerId) params.append('sellerId', filters.sellerId)
            
            const url = `/admin/get-dashboard-index-data${params.toString() ? '?' + params.toString() : ''}`
            const { data } = await api.get(url, { withCredentials: true })
            return data
        } catch (error) {
            return rejectWithValue(error.response.data)
        }
    }
)

export const dashboardIndexReducer = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        messageClear: (state) => {
            state.successMessage = ''
            state.errorMessage = ''
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(get_seller_dashboard_index_data.fulfilled, (state, action) => {
                state.totalSale = action.payload.totalSale
                state.totalOrder = action.payload.totalOrder
                state.totalProduct = action.payload.totalProduct
                state.totalPendingOrder = action.payload.totalPendingOrder
                state.totalPaidOrders = action.payload.totalPaidOrders || action.payload.paymentBreakdown?.totalPaidOrders || 0
                state.totalUnpaidOrders = action.payload.totalUnpaidOrders || action.payload.paymentBreakdown?.totalUnpaidOrders || 0
                state.totalRefundedOrders = action.payload.totalRefundedOrders || action.payload.paymentBreakdown?.totalRefundedOrders || 0
                state.totalPendingPaymentOrders = action.payload.paymentBreakdown?.totalPendingPaymentOrders || 0
                state.totalFailedPaymentOrders = action.payload.paymentBreakdown?.totalFailedPaymentOrders || 0
                state.totalDeliveredOrders = action.payload.totalDeliveredOrders
                state.totalCancelledOrders = action.payload.totalCancelledOrders
                state.totalProcessingOrders = action.payload.totalProcessingOrders
                state.totalNoShippedOrders = action.payload.totalNoShippedOrders || 0
                state.recentOrders = action.payload.recentOrders

                state.chartData = action.payload.chartData || {
                    orders: Array(12).fill(0),
                    revenue: Array(12).fill(0),
                    paidRevenue: Array(12).fill(0),
                    paidOrders: Array(12).fill(0),
                    unpaidOrders: Array(12).fill(0),
                    refundedOrders: Array(12).fill(0),
                    pendingPaymentOrders: Array(12).fill(0),
                    failedPaymentOrders: Array(12).fill(0),
                    deliveredOrders: Array(12).fill(0),
                    processingOrders: Array(12).fill(0),
                    cancelledOrders: Array(12).fill(0),
                    pendingOrders: Array(12).fill(0),
                    noShippedOrders: Array(12).fill(0)
                }
                state.monthlyStats = action.payload.monthlyStats || {
                    orderPercentage: 0,
                    revenuePercentage: 0,
                    productPercentage: 0
                }
                state.deliveryBreakdown = action.payload.deliveryBreakdown || state.deliveryBreakdown
            })
            .addCase(get_admin_dashboard_index_data.fulfilled, (state, action) => {
                state.totalSale = action.payload.totalSale
                state.totalOrder = action.payload.totalOrder
                state.totalProduct = action.payload.totalProduct
                state.totalSeller = action.payload.totalSeller
                state.recentOrders = action.payload.recentOrders
                state.chartData = action.payload.chartData || {
                    orders: Array(12).fill(0),
                    revenue: Array(12).fill(0),
                    paidOrders: Array(12).fill(0),
                    unpaidOrders: Array(12).fill(0),
                    deliveredOrders: Array(12).fill(0),
                    processingOrders: Array(12).fill(0),
                    cancelledOrders: Array(12).fill(0),
                    sellers: Array(12).fill(0)
                }
                state.orderStats = action.payload.orderStats || {
                    totalPaidOrders: 0,
                    totalUnpaidOrders: 0,
                    totalRefundedOrders: 0,
                    totalPendingPaymentOrders: 0,
                    totalFailedPaymentOrders: 0,
                    totalDeliveredOrders: 0,
                    totalProcessingOrders: 0,
                    totalCancelledOrders: 0,
                    totalPendingOrders: 0,
                    totalNoShippedOrders: 0
                }
                state.deliveryBreakdown = action.payload.deliveryBreakdown || state.deliveryBreakdown
                state.topSellers = action.payload.topSellers || []
                state.allSellers = action.payload.allSellers || []
            })
    }
})

export const { messageClear } = dashboardIndexReducer.actions
export default dashboardIndexReducer.reducer